import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { scrollToId } from "@/lib/lenis";

const HERO_IMG = "/images/jared-action.png";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0">
        <div className="absolute right-[-14%] sm:right-[-4%] lg:right-[2%] bottom-0 top-0 w-[135%] sm:w-[78%] lg:w-[52%]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(204,255,0,0.13),transparent_62%)]" />
          <motion.img
            src={HERO_IMG}
            alt="Jared Dupere — San Francisco Giants organization"
            style={{ y: imgY }}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full h-full object-contain object-bottom opacity-60 sm:opacity-90 lg:opacity-100"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0C] via-[#0B0B0C]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-transparent to-[#0B0B0C]/60" />
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 w-full"
      >
        <MaskedLine delay={0.3}>
          <p className="section-label mb-6" data-testid="hero-eyebrow">
            Jared Dupere — Former D1 &amp; SF Giants Org
          </p>
        </MaskedLine>
        <h1
          className="font-display uppercase text-white leading-[0.85] tracking-tight text-[19vw] sm:text-8xl lg:text-[9.5rem]"
          data-testid="hero-headline"
        >
          <MaskedLine delay={0.45}>1-On-1 Baseball</MaskedLine>
          <MaskedLine delay={0.58}>
            <span className="text-[#CCFF00]">Performance</span>
          </MaskedLine>
          <MaskedLine delay={0.71}>Coaching</MaskedLine>
        </h1>
        <MaskedLine delay={0.9}>
          <p
            className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed"
            data-testid="hero-subheadline"
          >
            Customized strength, power, and speed programming — built around your
            body, your position, your season. Direct coaching from a former
            Division 1 and professional baseball player. No templates. No
            guesswork.
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
        >
          <button
            onClick={() => scrollToId("#apply")}
            data-testid="hero-apply-cta"
            className="group relative bg-[#CCFF00] text-[#0B0B0C] font-bold uppercase tracking-[0.15em] text-sm px-10 py-5 overflow-hidden transition-transform duration-300 hover:-translate-y-0.5"
          >
            <span className="relative z-10 flex items-center gap-3">
              Apply For Coaching
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
            </span>
            <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
          </button>
          <span className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-mono2">
            Limited Roster — Application Only
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
}
